<template>
  <div class="signup-wrapper" @keyup.enter="onEnter">

    <!-- Retomada: lead aberto para o e-mail — o link foi para a caixa de entrada -->
    <div v-if="state === 'resume_notice'" class="signup-card text-center">
      <feather-icon icon="MailIcon" class="h-12 w-12 text-primary mb-2"/>
      <h3>{{ $t('signup_wizard.lead.resume_title') }}</h3>
      <p class="text-muted">{{ $t('signup_wizard.lead.resume_text') }}</p>
    </div>

    <!--
      E-mail com conta na plataforma: o cadastro continua, mas só pelo link do
      e-mail — é ele que prova que a conta é de quem está preenchendo.
    -->
    <div v-else-if="state === 'account_notice'" class="signup-card text-center">
      <feather-icon icon="MailIcon" class="h-12 w-12 text-primary mb-2"/>
      <h3>{{ $t('signup_wizard.lead.account_title') }}</h3>
      <p class="text-muted">{{ $t('signup_wizard.lead.account_text') }}</p>
      <b-button variant="outline-primary" class="mt-1" to="/login">
        {{ $t('signup_wizard.lead.email_in_use_login') }}
      </b-button>
    </div>

    <!-- Link de retomada inválido/expirado: pedir outro -->
    <div v-else-if="state === 'invalid_token'" class="signup-card text-center">
      <feather-icon icon="AlertTriangleIcon" class="h-12 w-12 text-warning mb-2"/>
      <h3>{{ $t('signup_wizard.invalid.title') }}</h3>
      <p class="text-muted">{{ $t('signup_wizard.invalid.text') }}</p>

      <template v-if="!resendSent">
        <b-form-group :label="$t('signup_wizard.invalid.email')" class="text-left mt-2">
          <b-form-input v-model="resendEmail" type="email"/>
        </b-form-group>
        <b-button variant="primary" class="w-full" :disabled="busy || !resendEmail" @click="resend">
          {{ $t('signup_wizard.invalid.resend') }}
        </b-button>
      </template>
      <b-alert v-else variant="success" show class="p-1 mt-2"><small>{{ $t('signup_wizard.invalid.sent') }}</small></b-alert>

      <b-button variant="outline-secondary" class="w-full mt-1" @click="startOver">
        {{ $t('signup_wizard.invalid.start_over') }}
      </b-button>
      <recaptcha-notice/>
    </div>

    <!-- Cadastro enviado -->
    <div v-else-if="state === 'success'" class="signup-card text-center">
      <feather-icon icon="CheckCircleIcon" class="h-12 w-12 text-success mb-2"/>
      <h3>{{ $t('signup_wizard.success.title') }}</h3>
      <p class="text-muted">{{ $t('signup_wizard.success.text') }}</p>
      <ol class="text-left text-muted signup-success-steps">
        <li>{{ $t('signup_wizard.success.next_review') }}</li>
        <li>{{ $t('signup_wizard.success.next_email') }}</li>
        <li>{{ $t('signup_wizard.success.next_login') }}</li>
      </ol>
      <b-button variant="primary" class="w-full mt-2" to="/login">
        {{ $t('signup_wizard.success.login') }}
      </b-button>
    </div>

    <!-- O wizard em si -->
    <div v-else class="signup-card">
      <h2 class="mb-0">{{ $t('signup_wizard.title') }}</h2>
      <p class="text-muted mb-2">{{ $t('signup_wizard.subtitle') }}</p>

      <b-alert v-if="serverAlert" variant="danger" show class="p-1 mb-2">
        <small>{{ serverAlert }}</small>
      </b-alert>
      <b-alert v-if="emailNotice" variant="info" show class="p-1 mb-2">
        <small>{{ $t('signup_wizard.lead.email_sent') }}</small>
      </b-alert>

      <validation-observer ref="observer">
        <wizard-shell
          :steps="shellSteps"
          :current="current"
          :doneKeys="doneSteps"
          :nextDisabled="nextDisabled"
          :nextLabel="current === 'lead' && !token ? $t('signup_wizard.lead.start') : null"
          :busy="busy"
          @prev="prev"
          @next="next"
          @goto="go">

          <!--
            Cada passo tem `key` própria, e isso NÃO é enfeite: os blocos são
            `<div>` irmãos sem key, então `sameVnode()` os considerava iguais e
            o Vue reaproveitava a árvore inteira ao trocar de passo — inclusive
            os `<input>`, levando junto as diretivas do passo anterior. Na
            prática, o campo "Nome público" (texto puro) herdava a `v-mask` de
            telefone do passo anterior e passava a formatar como telefone. Com
            key distinta o Vue destrói e recria, e a diretiva é desfeita.
          -->
          <div ref="stepBody">
            <!-- ============================== Passo 0: lead -->
            <div v-if="current === 'lead'" key="step-lead">
              <label class="d-block mb-1">{{ $t('signup_wizard.lead.type_label') }}</label>
              <choice-cards v-model="model.lead.person_type" :options="typeOptions" :disabled="!!token"/>

              <div class="vx-row mt-2">
                <div class="vx-col sm:w-1/2 w-full">
                  <validation-provider :name="$t('signup_wizard.lead.name_label')" vid="name" rules="required|min:3" v-slot="{ errors }">
                    <b-form-group :label="$t('signup_wizard.lead.name_label')">
                      <b-form-input ref="lead_name" v-model="model.lead.name" :state="errors.length ? false : null"/>
                      <small v-if="isEstablishment" class="text-muted">{{ $t('signup_wizard.lead.name_hint_le') }}</small>
                      <small class="text-danger d-block">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </div>
                <div class="vx-col sm:w-1/2 w-full">
                  <validation-provider :name="$t('signup_wizard.lead.email_label')" vid="email" rules="required|email" v-slot="{ errors }">
                    <b-form-group :label="$t('signup_wizard.lead.email_label')">
                      <!-- Lead vinculado a uma conta: trocar o e-mail aqui
                           mudaria de conta sem prova nenhuma, e o backend
                           recusa. Melhor não oferecer o campo editável. -->
                      <b-form-input
                        v-model="model.lead.email"
                        type="email"
                        :disabled="hasAccount"
                        :state="errors.length ? false : null"/>
                      <small class="text-muted">{{ $t('signup_wizard.lead.email_hint') }}</small>
                      <small class="text-danger d-block">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </div>
              </div>

              <p class="text-muted mb-0">
                <small>
                  {{ $t('signup_wizard.lead.privacy_notice') }}
                  <router-link to="/privacidade" target="_blank">{{ $t('signup_wizard.lead.privacy_link') }}</router-link>.
                </small>
              </p>
              <recaptcha-notice v-if="!token"/>
            </div>

            <!-- ============================== Empresa (só LE) -->
            <div v-if="current === 'company'" key="step-company">
              <div class="vx-row">
                <div class="vx-col sm:w-1/2 w-full">
                  <validation-provider name="CNPJ" vid="cnpj" rules="required|cnpj" v-slot="{ errors }">
                    <b-form-group :label="$t('signup_wizard.company.cnpj')">
                      <b-form-input v-model="model.company.cnpj" v-mask="'##.###.###/####-##'" inputmode="numeric" :state="errors.length ? false : null"/>
                      <small class="text-danger d-block">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </div>
                <div class="vx-col sm:w-1/2 w-full">
                  <validation-provider :name="$t('signup_wizard.company.company_name')" vid="company_name" rules="required|min:3" v-slot="{ errors }">
                    <b-form-group :label="$t('signup_wizard.company.company_name')">
                      <b-form-input v-model="model.company.company_name" :state="errors.length ? false : null"/>
                      <small class="text-danger d-block">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </div>
              </div>
              <validation-provider :name="$t('signup_wizard.company.trading_name')" vid="trading_name" rules="required|min:2" v-slot="{ errors }">
                <b-form-group :label="$t('signup_wizard.company.trading_name')">
                  <b-form-input v-model="model.company.trading_name" :state="errors.length ? false : null"/>
                  <small class="text-muted">{{ $t('signup_wizard.company.trading_name_hint') }}</small>
                  <small class="text-danger d-block">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
              <validation-provider :name="$t('signup_wizard.company.description')" vid="description" rules="required|min:10" v-slot="{ errors }">
                <b-form-group :label="$t('signup_wizard.company.description')">
                  <b-form-textarea v-model="model.company.description" rows="3" :state="errors.length ? false : null"/>
                  <small class="text-muted">{{ $t('signup_wizard.company.description_hint') }}</small>
                  <small class="text-danger d-block">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </div>

            <!-- ============================== Seus dados -->
            <div v-if="current === 'person'" key="step-person">
              <h4 class="mb-2">{{ $t(isEstablishment ? 'signup_wizard.person.title_le' : 'signup_wizard.person.title_ne') }}</h4>
              <div class="vx-row">
                <div class="vx-col sm:w-1/2 w-full">
                  <validation-provider name="CPF" vid="cpf" rules="required|cpf" v-slot="{ errors }">
                    <b-form-group :label="$t('signup_wizard.person.cpf')">
                      <b-form-input v-model="model.person.cpf" v-mask="'###.###.###-##'" inputmode="numeric" :state="errors.length ? false : null"/>
                      <small class="text-danger d-block">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </div>
                <div class="vx-col sm:w-1/2 w-full">
                  <validation-provider :name="$t('signup_wizard.person.birthdate')" vid="birthdate" rules="required|min_age" v-slot="{ errors }">
                    <b-form-group :label="$t('signup_wizard.person.birthdate')">
                      <b-form-input v-model="model.person.birthdate" type="date" :state="errors.length ? false : null"/>
                      <small class="text-muted">{{ $t('signup_wizard.person.birthdate_hint') }}</small>
                      <small class="text-danger d-block">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </div>
              </div>
              <validation-provider :name="$t('signup_wizard.person.phone')" vid="phone" rules="required|min:14" v-slot="{ errors }">
                <b-form-group :label="$t('signup_wizard.person.phone')">
                  <b-form-input v-model="model.person.phone" v-mask="['(##) ####-####', '(##) #####-####']" inputmode="tel" :state="errors.length ? false : null"/>
                  <small class="text-danger d-block">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </div>

            <!-- ============================== Atuação -->
            <div v-if="current === 'activity'" key="step-activity">
              <!-- Sem ValidationProvider aqui: provider não enxerga valor que não
                   vem de um input; quem trava o avanço sem modalidade é o
                   nextDisabled, e o backend revalida de qualquer forma. -->
              <b-form-group :label="$t('signup_wizard.activity.modalities')">
                <div class="signup-pills">
                  <button
                    v-for="modality in modalities"
                    :key="modality.id"
                    type="button"
                    class="signup-pill"
                    :class="{ 'is-selected': model.activity.modality_ids.includes(modality.id) }"
                    @click="toggleModality(modality.id)">
                    {{ modality.name }}
                  </button>
                </div>
                <small class="text-muted">{{ $t('signup_wizard.activity.modalities_hint') }}</small>
              </b-form-group>

              <template v-if="!isEstablishment">
                <validation-provider :name="$t('signup_wizard.activity.display_name')" vid="display_name" rules="required|min:3" v-slot="{ errors }">
                  <b-form-group :label="$t('signup_wizard.activity.display_name')">
                    <b-form-input v-model="model.activity.display_name" :state="errors.length ? false : null"/>
                    <small class="text-muted">{{ $t('signup_wizard.activity.display_name_hint') }}</small>
                    <small class="text-danger d-block">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
                <validation-provider :name="$t('signup_wizard.activity.description')" vid="description" rules="required|min:10" v-slot="{ errors }">
                  <b-form-group :label="$t('signup_wizard.activity.description')">
                    <b-form-textarea v-model="model.activity.description" rows="3" :state="errors.length ? false : null"/>
                    <small class="text-muted">{{ $t('signup_wizard.activity.description_hint') }}</small>
                    <small class="text-danger d-block">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </template>

              <b-form-group :label="$t(isEstablishment ? 'signup_wizard.activity.serve_title_le' : 'signup_wizard.activity.serve_title')">
                <b-form-checkbox v-if="!isEstablishment" v-model="model.activity.serves_in_person">
                  {{ $t('signup_wizard.activity.in_person') }}
                </b-form-checkbox>
                <b-form-checkbox v-model="model.activity.serves_at_home">
                  {{ $t('signup_wizard.activity.at_home') }}
                </b-form-checkbox>
                <b-form-checkbox v-model="model.activity.serves_online">
                  {{ $t('signup_wizard.activity.online') }}
                </b-form-checkbox>
                <small v-if="!isEstablishment && !anyServeMode" class="text-danger d-block mt-1">
                  {{ $t('signup_wizard.activity.serve_title') }}
                </small>
              </b-form-group>

              <b-form-group v-if="model.activity.serves_at_home" :label="$t('signup_wizard.activity.radius')">
                <b-form-input v-model.number="model.activity.service_radius_km" type="number" min="1" max="500" style="max-width: 10rem"/>
                <small class="text-muted">{{ $t('signup_wizard.activity.radius_hint') }}</small>
              </b-form-group>
            </div>

            <!-- ============================== Endereço -->
            <div v-if="current === 'address'" key="step-address">
              <div class="vx-row">
                <div class="vx-col sm:w-1/3 w-full">
                  <validation-provider name="CEP" vid="postal_code" rules="required|min:9" v-slot="{ errors }">
                    <b-form-group :label="$t('signup_wizard.address.postal_code')">
                      <b-form-input v-model="model.address.postal_code" v-mask="'#####-###'" inputmode="numeric" :state="errors.length ? false : null" @input="onCepInput"/>
                      <small v-if="cepStatus === 'searching'" class="text-muted">{{ $t('signup_wizard.address.searching') }}</small>
                      <small v-else-if="cepStatus === 'manual'" class="text-warning">{{ $t('signup_wizard.address.not_found') }}</small>
                      <small class="text-danger d-block">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </div>
                <div class="vx-col sm:w-2/3 w-full">
                  <validation-provider :name="$t('signup_wizard.address.street')" vid="street_name" rules="required" v-slot="{ errors }">
                    <b-form-group :label="$t('signup_wizard.address.street')">
                      <b-form-input v-model="model.address.street_name" :state="errors.length ? false : null"/>
                      <small class="text-danger d-block">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </div>
              </div>
              <div class="vx-row">
                <div class="vx-col sm:w-1/3 w-full">
                  <validation-provider :name="$t('signup_wizard.address.number')" vid="street_number" rules="required" v-slot="{ errors }">
                    <b-form-group :label="$t('signup_wizard.address.number')">
                      <b-form-input ref="street_number" v-model="model.address.street_number" :state="errors.length ? false : null"/>
                      <small class="text-danger d-block">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </div>
                <div class="vx-col sm:w-2/3 w-full">
                  <validation-provider :name="$t('signup_wizard.address.district')" vid="district_name" rules="required" v-slot="{ errors }">
                    <b-form-group :label="$t('signup_wizard.address.district')">
                      <b-form-input v-model="model.address.district_name" :state="errors.length ? false : null"/>
                      <small class="text-danger d-block">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </div>
              </div>

              <!-- Cidade fora do ValidationProvider pelo mesmo motivo das
                   modalidades: o valor não vem de um input. nextDisabled segura
                   o avanço até haver city_id. -->
              <b-form-group :label="$t('signup_wizard.address.city')">
                <div v-if="model.address.city_id" class="signup-city-selected">
                  <feather-icon icon="MapPinIcon" svgClasses="w-4 h-4"/>
                  <span>{{ model.address.city_label }}</span>
                  <b-button size="sm" variant="link" class="p-0 ml-1" @click="clearCity">
                    {{ $t('signup_wizard.address.change_city') }}
                  </b-button>
                </div>
                <template v-else>
                  <b-form-input v-model="cityQuery" :placeholder="$t('signup_wizard.address.city_placeholder')" autocomplete="off"/>
                  <div v-if="cityOptions.length" class="signup-city-options">
                    <button
                      v-for="city in cityOptions"
                      :key="city.id"
                      type="button"
                      class="signup-city-option"
                      @click="pickCity(city)">
                      {{ city.name }}/{{ city.uf }}
                    </button>
                  </div>
                </template>
              </b-form-group>
            </div>

            <!-- Sem passo de recebimento: o repasse é conta conectada do Stripe,
                 configurada no painel depois da aprovação. -->

            <!-- ============================== Acesso -->
            <div v-if="current === 'access'" key="step-access">
              <validation-provider :name="$t('signup_wizard.access.password')" vid="password" rules="required|min:8" v-slot="{ errors }">
                <b-form-group :label="$t('signup_wizard.access.password')">
                  <b-form-input v-model="model.access.password" type="password" autocomplete="new-password" :state="errors.length ? false : null"/>
                  <small class="text-muted">{{ $t('signup_wizard.access.policy') }}</small>
                  <small v-if="model.access.password && !passwordPolicyOk" class="text-danger d-block">{{ $t('signup_wizard.access.policy') }}</small>
                  <small class="text-danger d-block">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
              <validation-provider :name="$t('signup_wizard.access.confirmation')" vid="password_confirmation" rules="required" v-slot="{ errors }">
                <b-form-group :label="$t('signup_wizard.access.confirmation')">
                  <b-form-input v-model="model.access.password_confirmation" type="password" autocomplete="new-password" :state="errors.length ? false : null"/>
                  <small v-if="model.access.password_confirmation && !passwordsMatch" class="text-danger d-block">{{ $t('signup_wizard.access.mismatch') }}</small>
                  <small class="text-danger d-block">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </div>

            <!-- ============================== Revisão -->
            <div v-if="current === 'review'" key="step-review">
              <h4 class="mb-0">{{ $t('signup_wizard.review.title') }}</h4>
              <p class="text-muted mb-2"><small>{{ $t('signup_wizard.review.subtitle') }}</small></p>

              <div v-for="row in summaryRows" :key="row.step" class="signup-summary-row">
                <div>
                  <strong class="d-block">{{ $t(`signup_wizard.steps.${row.step}`) }}</strong>
                  <small class="text-muted">{{ row.text }}</small>
                </div>
                <b-button size="sm" variant="link" @click="go(row.step)">{{ $t('signup_wizard.review.edit') }}</b-button>
              </div>

              <div class="mt-2">
                <b-form-checkbox v-model="model.accepts.terms" class="mb-1">
                  {{ $t('signup_wizard.review.accept_terms_prefix') }}
                  <router-link to="/termos" target="_blank">{{ $t('signup_wizard.review.accept_terms_link') }}</router-link>
                  <small v-if="docs.terms" class="text-muted">v{{ docs.terms }}</small>
                </b-form-checkbox>
                <b-form-checkbox v-model="model.accepts.privacy" class="mb-1">
                  {{ $t('signup_wizard.review.accept_privacy_prefix') }}
                  <router-link to="/privacidade" target="_blank">{{ $t('signup_wizard.review.accept_privacy_link') }}</router-link>
                  <small v-if="docs.privacy" class="text-muted">v{{ docs.privacy }}</small>
                </b-form-checkbox>
              </div>

              <b-button variant="primary" class="w-full mt-2" :disabled="!canSubmit || busy" @click="submit">
                {{ busy ? $t('signup_wizard.review.submitting') : $t('signup_wizard.review.submit') }}
              </b-button>
              <p class="text-muted text-center mt-1 mb-0">
                <small>{{ hasAccount ? $t('signup_wizard.review.note_account') : $t('signup_wizard.review.note') }}</small>
              </p>
            </div>
          </div>

          <template #footer-info>
            <small v-if="savedAt" class="text-success">
              <feather-icon icon="CheckIcon" svgClasses="w-3 h-3"/> {{ $t('signup_wizard.saved') }}
            </small>
            <small v-else-if="token" class="text-muted">{{ $t('signup_wizard.resume_hint') }}</small>
          </template>
        </wizard-shell>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import {
  BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BButton, BAlert
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
// Import por efeito colateral: é ele que registra as regras (required, email,
// cpf, cnpj, min_age...) no vee-validate 3. Não copiar os idiomas do
// vee-validate 2 — foi o que já quebrou o Register.vue.
import '@core/utils/validations/validations'
import WizardShell from '@/components/catalog/WizardShell.vue'
import ChoiceCards from '@/components/catalog/ChoiceCards.vue'
import RecaptchaNotice from '@/components/RecaptchaNotice.vue'
import recaptchaMixin from '@/mixins/recaptcha'
import { LegalDocumentService } from '@/services/api/PrivacyService'
import { capture } from '@/util/posthog'
import axios from 'axios'
import {
  startLead, fetchLead, saveStep, submitLead, resendLink,
  searchCities, lookupCep, extractMessage, extractFieldErrors
} from '@/services/onboarding'

const TOKEN_STORAGE_KEY = 'hubee.onboarding.token'

/**
 * Auto-cadastro de parceiros — wizard público lead-first.
 *
 * O passo 0 (nome + e-mail + tipo) já cria o lead no backend e dispara o
 * e-mail de retomada; cada "Continuar" persiste o passo (autosave), e o
 * "Enviar" da revisão converte tudo nas entidades reais, que caem na fila de
 * aprovação da Hubee. Os campos de cada passo são o contrato da checklist de
 * aprovação — coletar menos deixaria o cadastro impossível de aprovar.
 *
 * Segurança/UX que não são óbvias no template:
 * - token de retomada vive em memória + sessionStorage e é LIMPO da URL no
 *   mount (o e-mail é o canal de retomada; URL não fica compartilhável);
 * - serviços de `services/api` (Rest) não são usados: o interceptor deles
 *   derruba a sessão em qualquer 401 — este fluxo é anônimo por definição;
 * - a senha nunca entra no autosave: só viaja no request final de submit.
 */
export default {
  name: 'PartnerSignupWizard',
  components: {
    BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BButton, BAlert,
    ValidationProvider, ValidationObserver,
    WizardShell, ChoiceCards, RecaptchaNotice
  },
  mixins: [recaptchaMixin],
  data() {
    return {
      state: 'form', // form | resume_notice | invalid_token | success
      token: null,
      lead: null,
      payloadSaved: {},
      doneSteps: [],
      current: 'lead',
      busy: false,
      savedAt: null,
      serverAlert: '',
      emailNotice: false,
      docs: { terms: null, privacy: null },
      modalities: [],
      cityQuery: '',
      cityOptions: [],
      citySearchTimer: null,
      cepStatus: null,
      resendEmail: '',
      resendSent: false,
      model: {
        lead: { name: '', email: '', person_type: null, source: 'DI' },
        company: { cnpj: '', company_name: '', trading_name: '', description: '' },
        person: { cpf: '', birthdate: '', phone: '' },
        activity: {
          modality_ids: [],
          display_name: '',
          description: '',
          serves_in_person: true,
          serves_at_home: false,
          serves_online: false,
          service_radius_km: null
        },
        address: { postal_code: '', street_name: '', street_number: '', district_name: '', city_id: null, city_label: '' },
        access: { password: '', password_confirmation: '' },
        accepts: { terms: false, privacy: false }
      }
    }
  },
  computed: {
    isEstablishment() {
      return this.model.lead.person_type === 'LE'
    },
    /** Lead vinculado a uma conta que já existe (veio pelo link do e-mail). */
    hasAccount() {
      return !!(this.lead && this.lead.has_account)
    },
    typeOptions() {
      return [
        {
          value: 'NE',
          title: this.$t('signup_wizard.lead.type_ne_title'),
          description: this.$t('signup_wizard.lead.type_ne_desc'),
          example: this.$t('signup_wizard.lead.type_ne_example'),
          icon: 'UserIcon'
        },
        {
          value: 'LE',
          title: this.$t('signup_wizard.lead.type_le_title'),
          description: this.$t('signup_wizard.lead.type_le_desc'),
          example: this.$t('signup_wizard.lead.type_le_example'),
          icon: 'HomeIcon'
        }
      ]
    },
    anyServeMode() {
      const activity = this.model.activity
      return activity.serves_in_person || activity.serves_at_home || activity.serves_online
    },
    needsAddress() {
      if (this.isEstablishment) return true
      return this.model.activity.serves_in_person || this.model.activity.serves_at_home
    },
    stepKeys() {
      if (!this.model.lead.person_type) return ['lead']

      const keys = ['lead']
      if (this.isEstablishment) keys.push('company')
      keys.push('person', 'activity')
      if (this.needsAddress) keys.push('address')
      // Quem já tem conta entra com a senha de sempre — o passo de criar senha
      // não existe para essa pessoa (e o backend recusaria trocá-la aqui).
      if (!this.hasAccount) keys.push('access')
      keys.push('review')
      return keys
    },
    shellSteps() {
      return this.stepKeys.map(key => ({ key, title: this.$t(`signup_wizard.steps.${key}`) }))
    },
    passwordPolicyOk() {
      const value = this.model.access.password
      return value.length >= 8 && /\p{Lu}/u.test(value) && /[^\p{L}\p{N}]/u.test(value)
    },
    passwordsMatch() {
      return this.model.access.password === this.model.access.password_confirmation
    },
    nextDisabled() {
      if (this.current === 'lead') return !this.model.lead.person_type
      if (this.current === 'activity') {
        if (!this.model.activity.modality_ids.length) return true
        return this.isEstablishment ? false : !this.anyServeMode
      }
      if (this.current === 'address') return !this.model.address.city_id
      if (this.current === 'access') return !this.passwordPolicyOk || !this.passwordsMatch
      return false
    },
    canSubmit() {
      const senhaOk = this.hasAccount || (this.passwordPolicyOk && this.passwordsMatch)

      return this.model.accepts.terms
        && this.model.accepts.privacy
        && this.docs.terms
        && this.docs.privacy
        && senhaOk
    },
    summaryRows() {
      const rows = []
      const model = this.model
      rows.push({ step: 'lead', text: `${model.lead.name} · ${model.lead.email}` })
      if (this.isEstablishment) {
        rows.push({ step: 'company', text: `${model.company.trading_name} · CNPJ ${model.company.cnpj}` })
      }
      rows.push({ step: 'person', text: `CPF ${model.person.cpf} · ${model.person.phone}` })

      const names = this.modalities
        .filter(modality => model.activity.modality_ids.includes(modality.id))
        .map(modality => modality.name)
      rows.push({ step: 'activity', text: names.join(', ') || '—' })

      if (this.stepKeys.includes('address')) {
        rows.push({
          step: 'address',
          text: `${model.address.street_name}, ${model.address.street_number} · ${model.address.city_label}`
        })
      }
      return rows
    }
  },
  watch: {
    current(step) {
      // A carga do mount pode ter falhado (API fora do ar, rede móvel): sem
      // modalidade o passo de atuação vira beco sem saída, e sem as versões dos
      // documentos a revisão não deixa enviar. Reentrar tenta de novo.
      if (step === 'activity' && !this.modalities.length) this.loadModalities()
      if (step === 'review' && (!this.docs.terms || !this.docs.privacy)) this.loadDocumentVersions()
      this.$nextTick(() => this.focusFirstField())
    },
    cityQuery(value) {
      clearTimeout(this.citySearchTimer)
      const query = (value || '').trim()
      if (query.length < 2) {
        this.cityOptions = []
        return
      }
      this.citySearchTimer = setTimeout(async () => {
        try {
          const { data } = await searchCities({ q: query })
          this.cityOptions = Array.isArray(data) ? data : []
        } catch (error) {
          this.cityOptions = []
        }
      }, 300)
    }
  },
  async mounted() {
    const query = this.$route.query || {}

    if (query.type === 'LE' || query.type === 'NE') this.model.lead.person_type = query.type
    if (query.src === 'mp') this.model.lead.source = 'MP'

    const tokenFromUrl = typeof query.token === 'string' ? query.token : null
    const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY)

    // O token sai da URL imediatamente: o canal de retomada é o e-mail, e a
    // URL limpa não vaza a sessão do wizard por histórico ou print.
    if (tokenFromUrl) {
      const cleaned = { ...query }
      delete cleaned.token
      this.$router.replace({ query: cleaned }).catch(() => {})
    }

    this.loadDocumentVersions()
    this.loadModalities()

    const token = tokenFromUrl || stored
    if (token) {
      await this.resume(token)
    } else {
      this.$nextTick(() => this.focusFirstField())
    }
  },
  methods: {
    // ------------------------------------------------------------- retomada
    async resume(token) {
      this.busy = true
      try {
        const { data } = await fetchLead(token)
        this.token = token
        sessionStorage.setItem(TOKEN_STORAGE_KEY, token)
        this.hydrate(data)
        capture('signup_wizard_resumed', { type: this.model.lead.person_type })
      } catch (error) {
        sessionStorage.removeItem(TOKEN_STORAGE_KEY)
        this.state = 'invalid_token'
      } finally {
        this.busy = false
      }
    },
    hydrate(data) {
      const lead = data.lead || {}
      this.lead = lead
      this.model.lead.name = lead.name || ''
      this.model.lead.email = lead.email || ''
      this.model.lead.person_type = lead.person_type || null
      this.model.lead.source = lead.source || 'DI'

      const payload = data.payload && !Array.isArray(data.payload) ? data.payload : {}
      this.payloadSaved = payload

      Object.keys(payload).forEach(step => {
        if (this.model[step] && payload[step] && typeof payload[step] === 'object') {
          this.model[step] = { ...this.model[step], ...payload[step] }
        }
      })

      this.doneSteps = ['lead', ...Object.keys(payload)]

      const saved = lead.current_step
      this.current = this.stepKeys.includes(saved) ? saved : this.firstIncompleteStep()
    },
    firstIncompleteStep() {
      const pending = this.stepKeys.find(key => key !== 'lead' && key !== 'review' && !this.payloadSaved[key] && key !== 'access')
      return pending || 'review'
    },

    // ------------------------------------------------------------ navegação
    stepAfter(key) {
      const index = this.stepKeys.indexOf(key)
      return this.stepKeys[Math.min(index + 1, this.stepKeys.length - 1)]
    },
    go(key) {
      if (!this.stepKeys.includes(key)) return
      this.current = key
      this.$router.replace({ query: { ...this.$route.query, step: key } }).catch(() => {})
    },
    prev() {
      const index = this.stepKeys.indexOf(this.current)
      if (index > 0) this.go(this.stepKeys[index - 1])
    },
    async next() {
      if (this.busy) return
      this.serverAlert = ''

      const valid = await this.$refs.observer.validate()
      if (!valid) return

      if (this.current === 'lead') return this.saveLeadStep()
      if (this.current === 'access') return this.go('review')
      if (this.current === 'review') return

      await this.persistCurrent()
    },
    onEnter(event) {
      const tag = event.target && event.target.tagName
      if (tag === 'TEXTAREA' || this.state !== 'form') return
      if (this.current === 'review') return
      this.next()
    },
    focusFirstField() {
      const body = this.$refs.stepBody
      if (!body) return
      const field = body.querySelector('input:not([type=hidden]):not([type=checkbox]), textarea')
      if (field && field.offsetParent !== null) field.focus()
    },

    // ------------------------------------------------------------- persistência
    async saveLeadStep() {
      this.busy = true
      try {
        if (!this.token) {
          const recaptchaToken = await this.getRecaptchaToken('onboarding')
          const { data } = await startLead({ ...this.model.lead, recaptcha_token: recaptchaToken })

          if (data.status === 'resumed_by_email') {
            this.state = 'resume_notice'
            return
          }

          // Conta existente: o backend não devolve token de propósito — o
          // cadastro segue pelo link do e-mail, na conta que a pessoa já tem.
          if (data.status === 'existing_account') {
            this.state = 'account_notice'
            return
          }

          this.token = data.token
          sessionStorage.setItem(TOKEN_STORAGE_KEY, this.token)
          this.lead = data.lead
          this.emailNotice = true
          this.markDone('lead')
          capture('signup_wizard_started', {
            type: this.model.lead.person_type,
            source: this.model.lead.source
          })
          this.go(this.stepAfter('lead'))
        } else {
          await saveStep(this.token, {
            step: 'lead',
            data: { name: this.model.lead.name, email: this.model.lead.email },
            next_step: this.stepAfter('lead')
          })
          this.touchSaved()
          this.go(this.stepAfter('lead'))
        }
      } catch (error) {
        this.applyServerErrors(error)
      } finally {
        this.busy = false
      }
    },
    stepData(step) {
      const model = this.model[step]
      if (step === 'activity') {
        const data = { modality_ids: model.modality_ids.slice(), serves_in_person: model.serves_in_person, serves_at_home: model.serves_at_home, serves_online: model.serves_online }
        if (model.serves_at_home && model.service_radius_km) data.service_radius_km = model.service_radius_km
        if (!this.isEstablishment) {
          data.display_name = model.display_name
          data.description = model.description
        }
        return data
      }
      if (step === 'address') {
        return {
          postal_code: model.postal_code,
          street_name: model.street_name,
          street_number: String(model.street_number),
          district_name: model.district_name,
          city_id: model.city_id,
          city_label: model.city_label
        }
      }
      return { ...model }
    },
    async persistCurrent() {
      const step = this.current
      const nextKey = this.stepAfter(step)

      this.busy = true
      try {
        const { data } = await saveStep(this.token, {
          step,
          data: this.stepData(step),
          next_step: nextKey
        })
        this.payloadSaved = (data && data.payload) || this.payloadSaved
        this.markDone(step)
        this.touchSaved()
        capture('signup_wizard_step_completed', { step, type: this.model.lead.person_type })
        this.go(nextKey)
      } catch (error) {
        this.applyServerErrors(error)
      } finally {
        this.busy = false
      }
    },
    markDone(step) {
      if (!this.doneSteps.includes(step)) this.doneSteps.push(step)
    },
    touchSaved() {
      this.savedAt = Date.now()
      clearTimeout(this._savedTimer)
      this._savedTimer = setTimeout(() => { this.savedAt = null }, 4000)
    },

    // ------------------------------------------------------------------ envio
    async submit() {
      if (!this.canSubmit || this.busy) return
      this.serverAlert = ''
      this.busy = true
      try {
        const payload = {
          accepted_terms_version: this.docs.terms,
          accepted_privacy_version: this.docs.privacy
        }

        // Conta existente não manda senha: ela entra com a que já tem, e o
        // backend recusa trocar senha a partir deste formulário público.
        if (!this.hasAccount) {
          payload.password = this.model.access.password
          payload.password_confirmation = this.model.access.password_confirmation
        }

        await submitLead(this.token, payload)
        sessionStorage.removeItem(TOKEN_STORAGE_KEY)
        capture('signup_wizard_submitted', { type: this.model.lead.person_type, source: this.model.lead.source })
        this.state = 'success'
      } catch (error) {
        this.applyServerErrors(error)
      } finally {
        this.busy = false
      }
    },

    // -------------------------------------------------------------- suporte
    applyServerErrors(error) {
      const fields = extractFieldErrors(error)
      const fieldKeys = Object.keys(fields)

      if (fieldKeys.length) {
        // Erro de completude aponta o passo que falta (ex.: "person") — leva a
        // pessoa direto para lá em vez de mostrar um alerta órfão na revisão.
        const stepWithError = this.stepKeys.find(key => fieldKeys.includes(key))
        if (stepWithError) {
          this.serverAlert = fields[stepWithError]
          this.go(stepWithError)
          return
        }

        // Os passos renderizam por v-if: o provider do campo só existe no passo
        // dono. Navega primeiro; setErrors depois do re-render.
        const ownerStep = this.stepKeys.find(key => {
          const data = this.stepData(key === 'lead' ? 'lead' : key)
          return key !== 'review' && fieldKeys.some(field => field in (data || {}))
        })
        if (ownerStep && ownerStep !== this.current) this.go(ownerStep)

        this.$nextTick(() => {
          if (this.$refs.observer) {
            this.$refs.observer.setErrors(
              fieldKeys.reduce((out, key) => {
                out[key] = [fields[key]]
                return out
              }, {})
            )
          }
        })
      }

      this.serverAlert = this.serverAlert || extractMessage(error, this.$t('signup_wizard.errors.generic'))
    },
    toggleModality(id) {
      const list = this.model.activity.modality_ids
      const index = list.indexOf(id)
      if (index >= 0) list.splice(index, 1)
      else list.push(id)
    },
    async onCepInput(value) {
      const digits = String(value || '').replace(/\D/g, '')
      if (digits.length !== 8) return

      this.cepStatus = 'searching'
      try {
        const { data } = await lookupCep(digits)
        if (!data || data.erro) {
          this.cepStatus = 'manual'
          return
        }

        if (data.logradouro) this.model.address.street_name = data.logradouro
        if (data.bairro) this.model.address.district_name = data.bairro

        const response = await searchCities({ ibge: data.ibge })
        const city = Array.isArray(response.data) && response.data[0]
        if (city) {
          this.model.address.city_id = city.id
          this.model.address.city_label = `${city.name}/${city.uf}`
          this.cepStatus = 'ok'
          const numberField = this.$refs.street_number
          if (numberField && numberField.focus) numberField.focus()
        } else {
          this.cepStatus = 'manual'
        }
      } catch (error) {
        this.cepStatus = 'manual'
      }
    },
    pickCity(city) {
      this.model.address.city_id = city.id
      this.model.address.city_label = `${city.name}/${city.uf}`
      this.cityOptions = []
      this.cityQuery = ''
    },
    clearCity() {
      this.model.address.city_id = null
      this.model.address.city_label = ''
    },
    async resend() {
      this.busy = true
      try {
        const recaptchaToken = await this.getRecaptchaToken('onboarding')
        await resendLink(this.resendEmail, recaptchaToken)
        this.resendSent = true
      } catch (error) {
        this.serverAlert = extractMessage(error, this.$t('signup_wizard.errors.generic'))
      } finally {
        this.busy = false
      }
    },
    startOver() {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY)
      this.token = null
      this.lead = null
      this.payloadSaved = {}
      this.doneSteps = []
      this.state = 'form'
      this.current = 'lead'
      this.$router.replace({ query: {} }).catch(() => {})
    },
    async loadDocumentVersions() {
      try {
        const service = LegalDocumentService.build()
        const documents = await service.documents()
        // O endpoint devolve { documents: [...], dpo: {...} }.
        const list = Array.isArray(documents)
          ? documents
          : (documents && (documents.documents || documents.data)) || []
        const terms = list.find(document => document.key === 'terms')
        const privacy = list.find(document => document.key === 'privacy')
        this.docs.terms = terms ? terms.version : null
        this.docs.privacy = privacy ? privacy.version : null
      } catch (error) {
        this.docs = { terms: null, privacy: null }
      }
    },
    async loadModalities() {
      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/v1/modalities/grid/pub`,
          { params: { per_page: 100 } }
        )
        const rows = (data && data.model && data.model.data) || (Array.isArray(data) ? data : [])
        this.modalities = rows.map(row => ({ id: row.id, name: row.name || row.description || `#${row.id}` }))
      } catch (error) {
        this.modalities = []
        this.serverAlert = this.$t('signup_wizard.errors.modality_load')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.signup-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem 4rem;
  background: #f8f8f8;
}

.signup-card {
  width: 100%;
  max-width: 780px;
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.w-full { width: 100%; }

.signup-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.signup-pill {
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: transparent;
  border-radius: 999px;
  padding: 0.35rem 0.9rem;
  cursor: pointer;
  font-size: 0.9rem;
  transition: border-color 0.15s ease, background 0.15s ease;

  &.is-selected {
    border-color: #7367f0;
    background: rgba(115, 103, 240, 0.12);
    color: #5e50ee;
    font-weight: 600;
  }
}

.signup-city-selected {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid rgba(40, 199, 111, 0.5);
  background: rgba(40, 199, 111, 0.08);
  border-radius: 0.5rem;
  padding: 0.35rem 0.75rem;
}

.signup-city-options {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0.5rem;
  margin-top: 0.25rem;
  overflow: hidden;
}

.signup-city-option {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 0.45rem 0.75rem;
  cursor: pointer;

  & + & { border-top: 1px solid rgba(0, 0, 0, 0.06); }

  &:hover { background: rgba(115, 103, 240, 0.08); }
}

.signup-summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.signup-success-steps {
  max-width: 26rem;
  margin: 0.5rem auto 0;
  padding-left: 1.5rem;

  li { margin-bottom: 0.35rem; }
}

@media (max-width: 575px) {
  .signup-card { padding: 1.25rem; }
}
</style>
